import { Injectable } from '@angular/core';

import { client } from '@api';
import {
  documentByIdDelete,
  documentByIdGet,
  documentByIdGetActivityUsage,
  documentByIdUpdateVisibility,
  documentsConfirm,
  documentsList,
  documentsOpen,
} from '@api/sdk.gen';
import type {
  ConfirmDocumentBatchRequest,
  ConfirmDocumentResult,
  DocumentListItem,
  DocumentListPage,
  DocumentVisibility,
  DocumentsListData,
  OpenDocumentBatchRequest,
} from '@api';

import { emptyOnUnsafeInput } from '@shared/input-safety/empty-on-unsafe-input';

import type { DocumentActivityUsage } from '../../models/document-activity-usage';
import {
  OWN_UPLOAD_POLL_INTERVAL_MS,
  OWN_UPLOAD_POLL_TIMEOUT_MS,
} from '../../constants/own-upload-file.constants';
import type {
  DocumentUploadBatchResult,
  DocumentUploadProgressPhase,
  OwnUploadFileProgress,
  OwnUploadProgressReason,
} from '../../models/document-upload-progress';
import type { UploadedDocumentRef } from '../../models/uploaded-document-ref';
import { toUploadProgressPhase } from '../../utils/to-upload-progress-phase.util';

/** Max concurrent presigned PUTs to the object store. */
const CONCURRENCY = 4;

/** The page and page size Kt.Api applies to a list request that names none: Kt.Api's `ListDocumentsQueryConstants`. */
export const DOCUMENT_LIST_DEFAULT_PAGE = 1;
export const DOCUMENT_LIST_DEFAULT_PAGE_SIZE = 10;

/** Client-composed filter/paging input for the document list — the generated list query shape. */
export type DocumentListQuery = NonNullable<DocumentsListData['query']>;

/**
 * The page a list screen gets for a query the input-safety rule refuses, as for a search with no matches.
 * The interceptor refuses unsafe markup in any query key or value, not only the search term. The paging is the
 * request's as sent, not validated here: a bad paging value on a safe query stays Kt.Api's 400.
 *
 * @param query The refused query.
 * @returns A fresh empty page with a total of 0, carrying the request's `page` and `pageSize`, else the server defaults.
 */
export function emptyDocumentsPage(query: DocumentListQuery): DocumentListPage {
  return {
    items: [],
    totalCount: 0,
    page: query.page ?? DOCUMENT_LIST_DEFAULT_PAGE,
    pageSize: query.pageSize ?? DOCUMENT_LIST_DEFAULT_PAGE_SIZE,
  };
}

/**
 * The subset of a hey-api `responseStyle: 'fields'` result this adapter reads. Mirrors the
 * `ProjectsApiService` taxonomy: `throwOnError: false` + `responseStyle: 'fields'` keeps the HTTP
 * status the confirm branch needs, where hey-api's `throwOnError: true` path throws the parsed
 * error body and loses it. `response` is the raw `HttpResponse` on 2xx and the `HttpErrorResponse`
 * on failure — carrying `.ok`/`.status` in both.
 */
interface FieldsResult<TData> {
  data?: TData;
  error?: unknown;
  response?: { readonly ok: boolean; readonly status: number };
}

type UploadEntry = {
  readonly index: number;
  readonly file: File;
  readonly fileName: string;
  readonly documentId: string;
  readonly uploadUrl: string;
};

@Injectable({ providedIn: 'root' })
export class DocumentsApiService {
  // Overridable in tests so polling can resolve without a real 5-minute wait.
  pollIntervalMs = OWN_UPLOAD_POLL_INTERVAL_MS;
  pollTimeoutMs = OWN_UPLOAD_POLL_TIMEOUT_MS;

  /**
   * The caller's document list.
   *
   * @returns The server's `DocumentListPage` verbatim, `totalCount` included (not re-derived from `items.length`);
   *   or, when the client or Kt.Api rejects the query as unsafe input, `emptyDocumentsPage` for it.
   * @throws {unknown} Every other failure: a non-2xx response (raw `HttpErrorResponse`) or a body-less 2xx.
   */
  list(query: DocumentListQuery = {}): Promise<DocumentListPage> {
    const listQuery = this.#buildListQuery(query);

    return emptyOnUnsafeInput(
      () =>
        documentsList({
          query: listQuery,
          throwOnError: false,
          responseStyle: 'fields' as const,
        }).then((result) => this.#unwrap(result)),
      () => emptyDocumentsPage(listQuery),
    );
  }

  /**
   * A single owned document by id via the generated `documentByIdGet`
   * (`GET /v1/documents/{id}`). Used by the upload poll to resolve each pending
   * document's status directly, rather than scanning the list's first page.
   *
   * @param id - the document id.
   * @returns the document's current `DocumentListItem` (status included).
   */
  get(id: string): Promise<DocumentListItem> {
    // `responseStyle: 'data'` does not narrow `documentByIdGet`'s return (it stays the
    // `DocumentListItem | { data, request, response }` union — same reason ProjectsApiService
    // uses 'fields'), so we take the 'fields' result and read `.data`.
    return documentByIdGet({
      path: { id },
      throwOnError: true,
      responseStyle: 'fields' as const,
    }).then((result) => result.data);
  }

  /**
   * Direct-to-store upload: opens a batch for presigned URLs, PUTs each file
   * straight to the object store, confirms the batch, then polls each confirmed
   * document by id until it reaches a terminal indexing state (Indexed/Failed).
   *
   * Open and confirm go through the generated client so auth interceptors apply; the PUT is
   * a bare `fetch` because the URL is presigned and its Content-Type is not signed.
   */
  async uploadWithProgress(
    files: readonly File[],
    onProgress?: (index: number, progress: OwnUploadFileProgress) => void,
  ): Promise<DocumentUploadBatchResult> {
    if (files.length === 0) {
      return { refs: [], succeededCount: 0, failedCount: 0, stillProcessingCount: 0 };
    }

    const openRequest: OpenDocumentBatchRequest = {
      files: files.map((file) => ({
        fileName: file.name,
        sizeBytes: file.size,
        contentType: file.type || undefined,
      })),
    };
    const openResponse = await documentsOpen({
      body: openRequest,
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => this.#unwrap(result));

    // The open response carries no filename, so pair each document with its file by request order.
    const entries: UploadEntry[] = openResponse.documents.map((document, index) => ({
      index,
      file: files[index]!,
      fileName: files[index]?.name ?? '',
      documentId: document.documentId,
      uploadUrl: document.uploadUrl,
    }));

    for (const entry of entries) {
      onProgress?.(entry.index, {
        phase: 'uploading',
        documentId: entry.documentId,
        fileName: entry.fileName,
      });
    }

    const succeededPuts = await this.#uploadToStore(entries, onProgress);
    if (succeededPuts.length === 0) {
      return { refs: [], succeededCount: 0, failedCount: files.length, stillProcessingCount: 0 };
    }

    const confirmedUploaded = await this.#confirmUploaded(succeededPuts, onProgress);
    if (confirmedUploaded.length === 0) {
      return { refs: [], succeededCount: 0, failedCount: files.length, stillProcessingCount: 0 };
    }

    const { refs, stillProcessingCount } = await this.#pollUntilIndexed(confirmedUploaded, onProgress);
    return {
      refs,
      succeededCount: refs.length,
      failedCount: files.length - refs.length - stillProcessingCount,
      stillProcessingCount,
    };
  }

  /**
   * How many of the teacher's own activities reference a document, via the generated
   * `documentByIdGetActivityUsage` (`GET /v1/documents/{id}/activity-usage`). Used by the
   * library delete flow to warn before removing a document that is still in use.
   *
   * @param id - the document id.
   * @returns the document's activity-usage count.
   */
  getActivityUsage(id: string): Promise<DocumentActivityUsage> {
    // As with `get`, `responseStyle: 'data'` does not narrow the generated return, so we take
    // the 'fields' result and read `.data`.
    return documentByIdGetActivityUsage({
      path: { id },
      throwOnError: true,
      responseStyle: 'fields' as const,
    }).then((result) => {
      const activityCount = result.data.activityCount;
      // Fail closed on a malformed body: `activityCount` is declared as a non-negative `integer` in
      // the schema, so anything that is not a whole number >= 0 (missing, non-numeric, negative, or
      // fractional) must NOT pass through as an implicit "unused" (0) — otherwise a document that is
      // actually in use would get the weaker filename-only confirmation. `Number.isFinite` would
      // wrongly admit `-1` and `2.5`, so guard with `Number.isInteger(...) && >= 0`. Throwing routes
      // it through the SAME catch path the delete dialog uses for a network/HTTP error, so the delete
      // is blocked and retryable.
      if (!(Number.isInteger(activityCount) && activityCount >= 0)) {
        throw new Error('Malformed activity-usage response: activityCount is not a non-negative integer');
      }
      return { activityCount };
    });
  }

  /**
   * Absolute URL for `GET /v1/documents/{id}:download` — the API responds with a 302 to a presigned
   * object-store GET. Mirrors {@link ActivityApiService.getAttachmentContentUrl}.
   */
  getDocumentContentUrl(documentId: string): string {
    return client.buildUrl({
      url: '/v1/documents/{id}:download',
      path: { id: documentId },
    });
  }

  delete(id: string): Promise<void> {
    return documentByIdDelete({
      path: { id },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => {
      this.#assertOk(result);
    });
  }

  /**
   * Sets sharing visibility for an owned document (`PATCH /v1/documents/{id}/visibility`).
   */
  updateVisibility(id: string, visibility: DocumentVisibility): Promise<DocumentListItem> {
    return documentByIdUpdateVisibility({
      path: { id },
      body: { visibility },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => this.#unwrap(result));
  }

  async #uploadToStore(
    entries: readonly UploadEntry[],
    onProgress?: (index: number, progress: OwnUploadFileProgress) => void,
  ): Promise<UploadEntry[]> {
    const succeeded: UploadEntry[] = [];

    await this.#runWithConcurrency(entries, CONCURRENCY, async (entry) => {
      try {
        const response = await fetch(entry.uploadUrl, { method: 'PUT', body: entry.file });
        if (response.ok) {
          succeeded.push(entry);
          return;
        }
      } catch {
        // Fall through to the failure path — a failed PUT is excluded from confirm.
      }

      onProgress?.(entry.index, {
        phase: 'failed',
        documentId: entry.documentId,
        fileName: entry.fileName,
      });
    });

    return succeeded;
  }

  async #confirmUploaded(
    succeededPuts: readonly UploadEntry[],
    onProgress?: (index: number, progress: OwnUploadFileProgress) => void,
  ): Promise<UploadEntry[]> {
    const confirmRequest: ConfirmDocumentBatchRequest = {
      documentIds: succeededPuts.map((entry) => entry.documentId),
      state: 'uploaded',
    };
    const confirmResponse = await documentsConfirm({
      body: confirmRequest,
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => this.#unwrap(result));

    const resultById = new Map(
      confirmResponse.documents.map((document) => [document.documentId, document.result]),
    );

    const confirmed: UploadEntry[] = [];
    for (const entry of succeededPuts) {
      if (resultById.get(entry.documentId) === 'uploaded') {
        confirmed.push(entry);
        onProgress?.(entry.index, {
          phase: 'uploaded',
          documentId: entry.documentId,
          fileName: entry.fileName,
        });
      } else {
        // rejectedTooLarge / rejectedContentType / notFound / unknown / missing — none indexes, so
        // exclude from polling. Only the two outcomes the dialog can explain carry a distinct reason;
        // everything else (a `notFound` re-confirm of an already-rejected record, and any result this
        // client predates) degrades to a bare failure — see #confirmFailureReason.
        const reason = this.#confirmFailureReason(resultById.get(entry.documentId));

        onProgress?.(entry.index, {
          phase: 'failed',
          documentId: entry.documentId,
          fileName: entry.fileName,
          ...(reason ? { reason } : {}),
        });
      }
    }

    return confirmed;
  }

  /**
   * The distinct per-file failure reason for a non-`uploaded` confirm outcome, or `undefined` for a
   * bare failure. Only the two outcomes the dialog can put into words carry a reason: an oversize
   * object (`too-large`) and a content-type mismatch (`rejected-content-type`). Everything else
   * degrades to a bare `failed`:
   * - `notFound` — including a re-confirm of an already-rejected, now-deleted record (S18): the
   *   teacher was already shown the rejection, so this second lookup must not surface as a new,
   *   different failure.
   * - any confirm result this client predates — an unrecognised string falls through `default`
   *   rather than throwing, so a future server-side outcome never breaks the upload flow.
   */
  #confirmFailureReason(
    result: ConfirmDocumentResult | undefined,
  ): OwnUploadProgressReason | undefined {
    switch (result) {
      case 'rejectedTooLarge':
        return 'too-large';
      case 'rejectedContentType':
        return 'rejected-content-type';
      default:
        return undefined;
    }
  }

  async #pollUntilIndexed(
    pending: readonly UploadEntry[],
    onProgress?: (index: number, progress: OwnUploadFileProgress) => void,
  ): Promise<{ refs: UploadedDocumentRef[]; stillProcessingCount: number }> {
    const refs = new Map<string, UploadedDocumentRef>();
    const pendingIds = new Set(pending.map((entry) => entry.documentId));
    const entryById = new Map(pending.map((entry) => [entry.documentId, entry]));
    const lastPhaseById = new Map<string, DocumentUploadProgressPhase>(
      pending.map((entry) => [entry.documentId, 'uploaded'] as const),
    );
    const deadline = Date.now() + this.pollTimeoutMs;

    while (pendingIds.size > 0) {
      // Poll each pending document directly by id, rather than the list's first page, so a
      // document pushed off page 1 by newer uploads can still be tracked to a terminal state.
      // Tolerate a per-id GET failure this round (transient 5xx, eventual-consistency 404): a
      // rejected id is simply absent from `statusById` → its status is `undefined` → the
      // "keep polling, no emit" branch handles it, and the `deadline` stays the real terminator.
      const documents = await Promise.all([...pendingIds].map((id) => this.get(id).catch(() => undefined)));
      const statusById = new Map(
        documents
          .filter((document): document is DocumentListItem => document !== undefined)
          .map((document) => [document.id, document.status] as const),
      );

      for (const id of [...pendingIds]) {
        const status = statusById.get(id);
        const phase = status === undefined ? undefined : toUploadProgressPhase(status);

        if (phase === undefined) {
          continue;
        }

        const entry = entryById.get(id)!;

        if (phase === 'done') {
          refs.set(id, { id, fileName: entry.fileName });
        }

        if (phase === 'done' || phase === 'failed') {
          pendingIds.delete(id);
        }

        if (lastPhaseById.get(id) !== phase) {
          lastPhaseById.set(id, phase);
          onProgress?.(entry.index, { phase, documentId: id, fileName: entry.fileName });
        }
      }

      // Safety valve: stop once past the deadline so a slow document cannot trap the dialog forever.
      if (pendingIds.size === 0 || Date.now() >= deadline) {
        break;
      }

      await this.#delay(this.pollIntervalMs);
    }

    // Our deadline, not the server's verdict. These documents are still indexing and will appear in the
    // library on their own, so they are reported as `processing` — calling them failures makes the
    // teacher re-upload (a duplicate document and a second Agno entry) or delete one mid hand-off.
    for (const id of pendingIds) {
      const entry = entryById.get(id)!;
      onProgress?.(entry.index, {
        phase: 'processing',
        documentId: id,
        fileName: entry.fileName,
        reason: 'timeout',
      });
    }

    return { refs: [...refs.values()], stillProcessingCount: pendingIds.size };
  }

  async #runWithConcurrency<T>(
    items: readonly T[],
    limit: number,
    worker: (item: T) => Promise<void>,
  ): Promise<void> {
    let cursor = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (cursor < items.length) {
        const item = items[cursor++]!;
        await worker(item);
      }
    });

    await Promise.all(runners);
  }

  #delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Drops empty/whitespace-only filters and forwards only the set fields, matching the old omit-empty behaviour. */
  #buildListQuery(query: DocumentListQuery): DocumentListQuery {
    const built: DocumentListQuery = {};

    const searchTerm = query.searchTerm?.trim();
    if (searchTerm) {
      built.searchTerm = searchTerm;
    }

    const fileType = query.fileType?.trim();
    if (fileType) {
      built.fileType = fileType;
    }

    if (query.status && query.status.trim()) {
      built.status = query.status;
    }

    if (query.visibility && query.visibility.trim()) {
      built.visibility = query.visibility;
    }

    if (query.page !== undefined) {
      built.page = query.page;
    }

    if (query.pageSize !== undefined) {
      built.pageSize = query.pageSize;
    }

    const sortBy = query.sortBy?.trim();
    if (sortBy) {
      built.sortBy = sortBy;
    }

    const sortDir = query.sortDir?.trim();
    if (sortDir) {
      built.sortDir = sortDir;
    }

    return built;
  }

  /**
   * Throws unless a hey-api `fields` result is a 2xx, so the failure surfaces to the caller.
   * On a non-2xx the raw `HttpErrorResponse` carried on `response` is thrown — it holds the HTTP
   * status and the problem-details body the upload dialog's error resolver reads (its
   * `instanceof HttpErrorResponse` guard fires on it), so migrating off raw `HttpClient` keeps that
   * behaviour intact. When no response reached the client at all (a failure before the request was
   * sent), a real `Error` carrying `result.error` as its cause is thrown — never a nullish value.
   *
   * Mirrors `ProjectsApiService.assertOk`: used by the void/204 `delete` path, where an absent body
   * is legitimate and must NOT be treated as a failure.
   */
  #assertOk(result: FieldsResult<unknown>): void {
    const { response } = result;

    if (response === undefined || response.ok !== true) {
      throw (
        (response as unknown) ??
        new Error('Document API request failed before a response was received.', {
          cause: result.error,
        })
      );
    }
  }

  /**
   * Like {@link DocumentsApiService.#assertOk}, but for the body-carrying calls (list, open,
   * confirm): after the 2xx check it also rejects a body-less 2xx. A body-less 2xx would otherwise
   * resolve `data` as `null`/`undefined` typed as a DTO and crash callers on `.items` /
   * `.documents`, so throw an explicit, diagnosable error instead of returning a falsely-typed value.
   */
  #unwrap<TData>(result: FieldsResult<TData>): TData {
    this.#assertOk(result);

    const { data } = result;
    if (data == null) {
      throw new Error('Document API returned a 2xx response with no body.');
    }

    return data;
  }
}
